/**
 * RankingVisualization - Main coordinator class
 */

import * as d3 from 'd3';
import { AthleteData, ProgressionPoint } from '../types/athlete.types';
import { StageNumber } from '../types/stage.types';
import { RankingMode } from '../types/config.types';
import { DataService } from '../services/DataService';
import { FilterService } from '../services/FilterService';
import { SearchService } from '../services/SearchService';
import { AnalyticsService } from '../services/AnalyticsService';
import { Visualization } from '../components/Visualization';
import { ProgressionLine } from '../components/ProgressionLine';
import { StageNavigation } from '../components/StageNavigation';
import { FilterPanel } from '../components/FilterPanel';
import { AthletePanel } from '../components/AthletePanel';
import { WelcomeDialog } from '../components/WelcomeDialog';
import { getCountryFlag } from '../utils/countryFlags';
import { formatTime } from '../utils/timeFormatter';
import { saveLastSearchedBib, getLastSearchedBib } from '../utils/storage';

export class RankingVisualization {
    // Services
    private dataService: DataService;
    private filterService: FilterService;
    private searchService: SearchService;
    private analytics: AnalyticsService;

    // Components
    private visualization: Visualization;
    private progressionLine: ProgressionLine;
    private stageNavigation: StageNavigation;
    private filterPanel: FilterPanel;
    private athletePanel: AthletePanel;

    // State
    private filteredData: AthleteData[] = [];
    private selectedAthlete: AthleteData | null = null;

    constructor() {
        // Initialize services
        this.dataService = new DataService();
        this.filterService = new FilterService();
        this.searchService = new SearchService();
        this.analytics = new AnalyticsService();

        // Initialize components
        this.visualization = new Visualization(
            (athlete) => this.selectAthlete(athlete),
            (event, athlete) => this.showTooltip(event, athlete),
            () => this.hideTooltip()
        );

        this.progressionLine = new ProgressionLine();

        this.stageNavigation = new StageNavigation(
            this.analytics,
            (stage) => this.handleStageChange(stage)
        );

        this.filterPanel = new FilterPanel(
            this.analytics,
            () => this.handleFilterChange(),
            (mode) => this.handleRankingModeChange(mode)
        );

        this.athletePanel = new AthletePanel(this.filterService, this.dataService);

        // Initialize welcome dialog (will be shown after data loads)
        new WelcomeDialog(
            this.analytics,
            (bib) => this.searchWithAutoFilters(bib),
            () => this.stageNavigation.startPlay()
        );

        this.init();
    }

    private async init(): Promise<void> {
        this.visualization.showLoading();

        try {
            await this.dataService.loadData();
            console.log('Data loaded, initializing UI...');
            this.setupSearchListener();

            // Initialize the stage to 1 (this will trigger visualization update)
            this.stageNavigation.setStage(1);

            // Initialize progression line after visualization is rendered
            const vizGroup = this.visualization.getGroup();
            if (vizGroup) {
                this.progressionLine.init(vizGroup);
            }

            this.restoreLastSearch();
            console.log('Initialization complete');
        } catch (error) {
            console.error('Error during initialization:', error);
            this.visualization.showError('Error loading race data. Please ensure race_results.csv is available.');
        }
    }

    private handleStageChange(_stage: StageNumber): void {
        this.updateVisualization();
        this.updateSelectedAthletePanel();
    }

    private handleFilterChange(): void {
        this.updateVisualization();
        this.updateProgressionLine();
    }

    private handleRankingModeChange(_mode: RankingMode): void {
        this.updateVisualization();
        this.updateProgressionLine();
    }

    private updateVisualization(): void {
        const currentStage = this.stageNavigation.getCurrentStage();
        const stageData = this.dataService.getStageData(currentStage);
        const filters = this.filterService.getCurrentFilters();
        const rankingMode = this.filterPanel.getRankingMode();

        this.filteredData = this.filterService.applyFilters(stageData, filters, rankingMode);

        this.visualization.update(
            this.filteredData,
            rankingMode,
            currentStage,
            this.selectedAthlete?.Bib || null
        );

        if (this.selectedAthlete) {
            this.progressionLine.raise();
        }
    }

    private selectAthlete(athlete: AthleteData): void {
        this.selectedAthlete = athlete;

        d3.selectAll('.athlete-bar')
            .classed('selected', d => (d as any).Bib === athlete.Bib);

        const currentStage = this.stageNavigation.getCurrentStage();
        this.analytics.logAthleteSelected(
            athlete.Bib,
            athlete.Event,
            athlete.Distance,
            currentStage
        );

        this.updateSelectedAthletePanel();
    }

    private updateSelectedAthletePanel(): void {
        if (!this.selectedAthlete) return;

        const currentAthleteData = this.filteredData.find(
            d => d.Bib === this.selectedAthlete!.Bib
        );

        if (!currentAthleteData) {
            this.athletePanel.update(null, null, 0, this.stageNavigation.getCurrentStage());
            this.progressionLine.hide();
            return;
        }

        this.athletePanel.update(
            this.selectedAthlete,
            currentAthleteData,
            this.filteredData.length,
            this.stageNavigation.getCurrentStage()
        );

        this.updateProgressionLine();
    }

    private updateProgressionLine(): void {
        if (!this.selectedAthlete) {
            this.progressionLine.hide();
            return;
        }

        const progression = this.getAthleteProgression(this.selectedAthlete.Bib);
        if (progression.length < 2) {
            this.progressionLine.hide();
            return;
        }

        const xScale = this.visualization.getXScale();
        const barConfig = this.visualization.getBarConfig();

        if (xScale) {
            this.progressionLine.update(
                progression,
                xScale,
                barConfig.height,
                barConfig.padding,
                this.filterPanel.getRankingMode(),
                this.stageNavigation.getCurrentStage()
            );
        }
    }

    private getAthleteProgression(bib: number): ProgressionPoint[] {
        const progression: ProgressionPoint[] = [];
        const filters = this.filterService.getCurrentFilters();
        const rankingMode = this.filterPanel.getRankingMode();
        const currentStage = this.stageNavigation.getCurrentStage();

        for (let stage = 1; stage <= currentStage; stage++) {
            let stageData = this.dataService.getStageData(stage as StageNumber);
            stageData = this.filterService.applyFilters(stageData, filters, rankingMode);

            const athlete = stageData.find(d => d.Bib === bib);
            if (athlete) {
                const timeValue = rankingMode === 'individual'
                    ? (athlete.individualRaceTime || 0)
                    : athlete.stageTime;
                progression.push({
                    stage,
                    rank: athlete.filteredRank!,
                    time: timeValue
                });
            }
        }

        return progression;
    }

    private setupSearchListener(): void {
        d3.select('#athlete-search').on('input', (event) => {
            const searchTerm = (event.target as HTMLInputElement).value.trim();
            this.highlightSearchResults(searchTerm);

            if (searchTerm) {
                saveLastSearchedBib(searchTerm);

                const isNumber = !isNaN(parseInt(searchTerm));
                this.analytics.logAthleteSearch(
                    isNumber ? 'bib' : 'name',
                    this.selectedAthlete !== null
                );
            }
        });
    }

    private highlightSearchResults(searchTerm: string): void {
        if (!searchTerm) return;

        const matchedAthlete = this.searchService.findAthleteInData(searchTerm, this.filteredData);
        if (matchedAthlete) {
            this.selectAthlete(matchedAthlete);
        }
    }

    private searchWithAutoFilters(searchTerm: string): void {
        if (!searchTerm) return;

        const bibNumber = parseInt(searchTerm);
        const isValidBib = !isNaN(bibNumber) && bibNumber > 0;

        if (isValidBib) {
            const athlete = this.searchService.findAthleteAcrossStages(
                searchTerm,
                this.dataService.getAllData()
            );

            if (athlete) {
                this.searchService.applyFiltersForAthlete(athlete);
                this.updateVisualization();

                const matchedAthlete = this.filteredData.find(d => d.Bib === bibNumber);
                if (matchedAthlete) {
                    this.selectAthlete(matchedAthlete);
                }
            }
        }
    }

    private restoreLastSearch(): void {
        const lastBib = getLastSearchedBib();
        if (lastBib) {
            d3.select('#athlete-search').property('value', lastBib);
        }
    }

    private showTooltip(event: MouseEvent, athlete: AthleteData): void {
        const tooltip = d3.select('#tooltip');
        const currentStage = this.stageNavigation.getCurrentStage();
        const countryFlag = getCountryFlag(athlete.Country);
        const raceTime = formatTime(athlete.individualRaceTime || 0);
        const cumulativeTime = formatTime(athlete.stageTime);

        // Calculate distance-only cumulative rank (comparing only same distance)
        const stageData = this.dataService.getStageData(currentStage);
        const distanceRank = this.filterService.getDistanceOnlyRank(stageData, athlete);

        tooltip.html(`
            <h4>${countryFlag} ${athlete.Name}</h4>
            <p><strong>Bib:</strong> ${athlete.Bib}</p>
            <p><strong>Category:</strong> ${athlete.Event}</p>
            <p><strong>Distance:</strong> ${athlete.Distance} km</p>
            <p><strong>Age Group:</strong> ${athlete['Age Group']}</p>
            <hr style="margin: 0.5rem 0; border-color: #404040;">
            <p><strong>Race ${currentStage} Position:</strong> #${athlete.individualRaceRank} (${raceTime})</p>
            <p><strong>Overall Cumulative (${athlete.Distance}km):</strong> #${distanceRank} (${cumulativeTime})</p>
        `);

        tooltip
            .style('display', 'block')
            .style('left', (event.pageX + 10) + 'px')
            .style('top', (event.pageY - 10) + 'px');
    }

    private hideTooltip(): void {
        d3.select('#tooltip').style('display', 'none');
    }
}
